import React, { createContext, useCallback, useContext, useMemo, useRef } from "react";
import { createSearchParams, type NavigateOptions, type URLSearchParamsInit } from "react-router-dom";

// Profil klientki może być otwarty w kilku kartach naraz (TraineeWorkspace). Każda karta
// dostaje własny zakres: id klientki, informację, czy jest widoczna, i swoje parametry widoku.
// Komponenty profilu biorą id stąd, a nie z `useParams()` ani `store.trainee.profile`.

interface TraineeScopeValue {
  traineeId: string;
  // Widoczna karta. Tylko ona może załadować klientkę do Reduxa (builder).
  isActive: boolean;
  // Parametry widoku karty: dla widocznej to `location.search`, dla karty w tle jej zapamiętany stan.
  search: string;
  // Zapis parametrów - do URL, jeśli karta jest właścicielem adresu, inaczej do pamięci karty.
  setSearch: (search: string, options?: NavigateOptions) => void;
}

type TraineeNavigate = (to: string, options?: NavigateOptions) => void;

const TraineeScopeContext = createContext<TraineeScopeValue | null>(null);
// Osobny, niezmienny kontekst: przełączenie karty zmienia `isActive` i `search`, a większość
// komponentów potrzebuje tylko id i nie powinna się wtedy renderować.
const TraineeIdentityContext = createContext<{ traineeId: string; navigate: TraineeNavigate } | null>(null);

interface TraineeScopeProviderProps extends TraineeScopeValue {
  // Stabilna nawigacja - `useNavigate()` renderuje komponent przy każdej zmianie URL, we wszystkich kartach.
  navigate: TraineeNavigate;
  children: React.ReactNode;
}

export const TraineeScopeProvider = ({
  traineeId,
  isActive,
  search,
  setSearch,
  navigate,
  children,
}: TraineeScopeProviderProps) => {
  const value = useMemo(() => ({ traineeId, isActive, search, setSearch }), [traineeId, isActive, search, setSearch]);
  const identity = useMemo(() => ({ traineeId, navigate }), [traineeId, navigate]);

  return (
    <TraineeIdentityContext.Provider value={identity}>
      <TraineeScopeContext.Provider value={value}>{children}</TraineeScopeContext.Provider>
    </TraineeIdentityContext.Provider>
  );
};

export const useTraineeScope = () => {
  const scope = useContext(TraineeScopeContext);

  if (!scope) {
    throw new Error("useTraineeScope must be used inside TraineeScopeProvider");
  }

  return scope;
};

/** Jak `useTraineeScope`, ale poza kartą klientki zwraca `null` (komponent używany też gdzie indziej). */
export const useOptionalTraineeScope = () => useContext(TraineeScopeContext);

const useTraineeIdentity = () => {
  const identity = useContext(TraineeIdentityContext);

  if (!identity) {
    throw new Error("useTraineeId must be used inside TraineeScopeProvider");
  }

  return identity;
};

export const useTraineeId = () => useTraineeIdentity().traineeId;

/** Zamiennik `useNavigate()` w profilu, który nie renderuje komponentu przy każdej zmianie URL. */
export const useTraineeNavigate = () => useTraineeIdentity().navigate;

type SetSearchParams = (
  nextInit: URLSearchParamsInit | ((prev: URLSearchParams) => URLSearchParamsInit),
  navigateOptions?: NavigateOptions,
) => void;

/**
 * Zamiennik `useSearchParams()` dla komponentów profilu, z tą samą sygnaturą. Celowo nie
 * subskrybuje routera: przy 10 kartach każda zmiana URL renderowałaby wszystkie karty, a tak
 * nowe parametry dostaje tylko widoczna karta (podaje je TraineeWorkspace).
 *
 * O tym, czy zapis trafia do URL, decyduje TraineeWorkspace w chwili wywołania, nie renderu:
 * akcja asynchroniczna rozpoczęta w karcie A (np. generowanie diety) może skończyć się, gdy
 * widoczna jest już karta B, i wtedy jej `planId` nie może trafić do URL karty B.
 */
export const useTraineeSearchParams = (): [URLSearchParams, SetSearchParams] => {
  const { search, setSearch } = useTraineeScope();
  const searchRef = useRef(search);
  searchRef.current = search;

  const params = useMemo(() => new URLSearchParams(search), [search]);

  const setParams = useCallback<SetSearchParams>(
    (nextInit, navigateOptions) => {
      const next = typeof nextInit === "function" ? nextInit(new URLSearchParams(searchRef.current)) : nextInit;
      setSearch(`?${createSearchParams(next).toString()}`, navigateOptions);
    },
    [setSearch],
  );

  return [params, setParams];
};
